import type React from 'react'
import {Link} from '@primer/react'
import TipsContentList from './TipsContentList'
import styles from './data.module.css'
import {provideFeedback} from '../../hooks/use-search-bar-events'
import type {SearchKind, SearchKindCount} from '../../hooks/use-search-result-counts'
export interface NoResultDataItem {
  content: React.ReactNode
  name: string
  show: Array<SearchKindCount['name']>
}

export function generateTips({resultKind}: {resultKind: SearchKind}): NoResultDataItem[] {
  return generate().filter(t => t.show.includes(resultKind.name))
}

function generate(): NoResultDataItem[] {
  return [
    {
      name: 'Search across repositories',
      show: [
        'ai_resources',
        'code',
        'codelegacy',
        'issues',
        'pullrequests',
        'commits',
        'discussions',
        'registrypackages',
        'marketplace',
        'topics',
        'wikis',
        'users',
      ],
      content: (
        <>
          <TipsContentList
            data={[
              {
                name: 'Within a repository:',
                value: (
                  <>
                    <span className={styles.accentText}>repo:</span>
                    github/linguist
                  </>
                ),
              },
              {
                name: 'Across several:',
                value: (
                  <>
                    <span className={styles.accentText}>repo:</span>
                    github/linguist <span className={styles.doneText}>OR</span>{' '}
                    <span className={styles.accentText}>repo:</span>
                    github/fetch
                  </>
                ),
              },
            ]}
          />
          <div className={styles.noteBox}>
            Note that we don&apos;t currently support regular expressions in the repo or org qualifiers. For more
            information on search syntax, see our{' '}
            <Link
              inline
              href="https://docs.github.com/search-github/github-code-search/understanding-github-code-search-syntax"
            >
              syntax guide
            </Link>
            .
          </div>
        </>
      ),
    },
    {
      name: 'Search across an organization',
      show: [
        'ai_resources',
        'code',
        'codelegacy',
        'repositories',
        'issues',
        'pullrequests',
        'commits',
        'discussions',
        'registrypackages',
        'marketplace',
        'topics',
        'wikis',
        'users',
      ],
      content: (
        <>
          <TipsContentList
            data={[
              {
                name: 'Within an organization:',
                value: (
                  <>
                    <span className={styles.accentText}>org:</span>
                    rails
                  </>
                ),
              },
              {
                name: `User's code:`,
                value: (
                  <>
                    <span className={styles.accentText}>user:</span>
                    mona
                  </>
                ),
              },
            ]}
          />
          <div className={styles.noteBox}>
            Note that we don&apos;t currently support regular expressions in the repo or org qualifiers. For more
            information on search syntax, see our{' '}
            <Link
              inline
              href="https://docs.github.com/search-github/github-code-search/understanding-github-code-search-syntax"
            >
              syntax guide
            </Link>
            .
          </div>
        </>
      ),
    },
    {
      name: 'Find a particular file extension',
      show: ['code', 'codelegacy'],
      content: (
        <>
          <TipsContentList
            data={[
              {
                name: 'With .txt extensions:',
                value: (
                  <>
                    <span className={styles.accentText}>path:</span>
                    *.txt
                  </>
                ),
              },
              {
                name: `JavaScript and TypeScript files:`,
                value: (
                  <>
                    <span className={styles.accentText}>path:</span>
                    *.js <span className={styles.doneText}>OR</span> <span className={styles.accentText}>path:</span>
                    *.ts
                  </>
                ),
              },
            ]}
          />
          <div className={styles.noteBox}>
            The path qualifier can search the entire file path, not just the extension, and supports regular
            expressions. For more information, see our{' '}
            <Link
              inline
              href="https://docs.github.com/search-github/github-code-search/understanding-github-code-search-syntax"
            >
              syntax guide
            </Link>
            .
          </div>
        </>
      ),
    },
    {
      name: "Why wasn't my code found?",
      show: ['code'],
      content: (
        <>
          <p className={styles.smallParagraph}>
            When you search within a repository for the first time, please note that the repository undergoes indexing.{' '}
            <span className={styles.boldText}>This process may take a few minutes.</span>{' '}
          </p>
          <p className={styles.blockParagraph}>
            The index currently includes more than 70 million popular public repositories, plus all private repositories
            that users search for.
          </p>
          <p>Beyond that, we also don&apos;t include all files in the search index:</p>
          <p className={styles.indentedList}>
            <ul>
              <li>Vendored and generated code is excluded</li>
              <li>Empty files and files over 350 kiB are excluded</li>
              <li>Only UTF-8 encoded files are indexed</li>
              <li>Very large repositories may not be indexed</li>
            </ul>
          </p>

          <p className={styles.mutedSmallParagraph}>
            We intend to continue to increase the amount of code available in the index as much as possible. If we are
            missing files that are useful to you, feel free to{' '}
            <Link
              inline
              href="#"
              onClick={(e: React.MouseEvent) => {
                provideFeedback()
                e.preventDefault()

                // NOTE: This is necessary to prevent the dialog from instantly closing
                e.stopPropagation()
              }}
            >
              provide feedback here
            </Link>
            .
          </p>
        </>
      ),
    },
    {
      name: "Why wasn't my code found?",
      show: ['codelegacy'],
      content: (
        <>
          <p className={styles.smallParagraph}>
            In rare cases your file might not be indexed, for example we don&apos;t index
          </p>
          <p className={styles.indentedList}>
            <ul>
              <li>Empty files and files over 350 kiB are excluded</li>
              <li>Very large repositories may not be indexed</li>
            </ul>
          </p>
        </>
      ),
    },
    {
      name: 'Regular expressions',
      show: ['code', 'codelegacy'],
      content: (
        <>
          <TipsContentList
            data={[
              {
                name: 'Sparse followed by index:',
                value: '/sparse.*index/',
              },
              {
                name: `Lines that end with return:`,
                value: '/return;$/',
              },
              {
                name: `File paths matching:`,
                value: (
                  <>
                    <span className={styles.accentText}>path:</span>
                    /^\.github\/workflows\/.*\.yaml$/
                  </>
                ),
              },
            ]}
          />
          <div className={styles.noteBox}>
            Note that you&apos;ll have to escape any slashes in the regex. For more information, see our{' '}
            <Link
              inline
              href="https://docs.github.com/search-github/github-code-search/understanding-github-code-search-syntax"
            >
              syntax guide
            </Link>
            .
          </div>
        </>
      ),
    },
    {
      name: 'Saved searches',
      show: [
        'ai_resources',
        'code',
        'codelegacy',
        'repositories',
        'issues',
        'pullrequests',
        'commits',
        'discussions',
        'registrypackages',
        'marketplace',
        'topics',
        'wikis',
        'users',
      ],
      content:
        'Always searching within the same organization or set of repositories? Try constructing a query and click the save button in the top right corner.',
    },
  ]
}
